import { action, computed, observable } from 'mobx';
import user from '../../App/stores/UserStore';
import { WebSocketStore } from '../../App/stores/WebSocketStore';

import SaleUiStore from './SaleUiStore';
import SaleEditStore from './SaleEditStore';
import OffersStore from './OffersStore';
import RoundStore from './RoundStore';
import CommandBarStore from './CommandBarStore';
import { post } from 'services/request';
import BiddingStore from './BiddingStore';
import WhitelistingStore from './WhitelistingStore';
import { prepareSaleConfig } from '../../../helpers/prepareConfig';

const defaultStatus = 'NotStarted';

class SaleStore {
  @observable saleAgentData = {};
  @observable mls = null;
  @observable inviteOnly = false;
  @observable features = null;
  @observable integrations = null;

  @computed get isMls() {
    return this.mls && this.mls !== 'none';
  }

  @computed get isTest() {
    return this.features && this.features.test;
  }

  @observable status = defaultStatus;
  @observable isValid = false;

  @observable saleId = null;
  @observable item = {};

  @observable response = {};
  @observable error = null;

  @computed get roundDefinition() {
    return this.round.definition;
  }

  @computed get isDeleted() {
    return this.status === 'deleted';
  }

  @computed get isClosed() {
    return this.status === 'closed';
  }

  @computed get isComplete() {
    return this.isValid;
  }

  @computed get hasSalePermissions() {
    return this.user.hasPermissions(this.saleEdit.agentData);
  }

  @computed get hasExclusiveOfferPermissions() {
    const selectedOffer = this.offers.selectedOffer;
    return selectedOffer && this.user.hasPermissions(selectedOffer.agentData);
  }

  @computed get hasOfferPermissions() {
    return this.hasSalePermissions || this.hasExclusiveOfferPermissions;
  }

  @computed get saleConfig() {
    return prepareSaleConfig(
      this.user.app.config,
      this.user.app.data,
      this.user.claims,
      this
    );
  }

  constructor(user, socket) {
    this.user = user;
    this.socket = socket;
    this.socket.received = (message) => this.received(message);

    this.ui = new SaleUiStore({ root: this });
    this.saleEdit = new SaleEditStore({ root: this });
    this.offers = new OffersStore({ root: this });
    this.round = new RoundStore({ root: this });
    this.cmdBar = new CommandBarStore({ root: this });
    this.bidding = new BiddingStore({ offers: this.offers });
    this.whitelisting = new WhitelistingStore({ root: this });
  }

  @action start(item) {
    return post('/api/v1/sales', item, this.user.accessToken).then(
      this.success,
      this.failed
    );
  }

  @action.bound success(body) {
    this.response = body;
    this.error = null;
  }

  @action.bound failed(err) {
    this.error = {
      message: err.message,
      code:
        !!err.response && !!err.response.result
          ? err.response.result.error
          : '',
    };
  }

  @action received(message) {
    if (message.offers !== undefined) {
      this.offers.update(message.offers);
      if (!!message.offers.lastCorrelationUpdateId) {
        this.ui.loading.done(message.offers.lastCorrelationUpdateId);
      }
    }
    if (!!message.openBidding) {
      this.bidding.update(message.openBidding);
    }
    if (!!message.whitelisting) {
      this.whitelisting.update(message.whitelisting);
    }
    if (!message.sale) {
      return;
    }

    const {
      status,
      isValid,
      isValidExplanation,
      item,
      round,
      saleId,
      currencyCode,
      locale,
      offerData,
      agentData,
      docs,
      notifications,
      mls,
      features,
      integrations,
      invitedSeller,
      invitedSellerBlocked,
      inviteOnly,
      whitelistedUsers,
      ...rest
    } = message.sale;
    if (this.status !== status) {
      this.status = status;
    }
    if (item && item.updatedAt !== this.item.updatedAt) {
      this.item = item;
    }
    if (round && round.updatedAt !== this.round.definition.updatedAt) {
      this.round.definition = round;
    }
    if (this.isValid !== isValid) {
      this.isValid = isValid;
    }
    if (currencyCode) {
      this.saleEdit.currencyCode = currencyCode;
    }
    if (locale) {
      this.saleEdit.locale = locale;
    }
    if (offerData) {
      this.saleEdit.offerData = offerData;
    }
    if (agentData) {
      // construct agentData for compatibility
      const enhancedAgentData = {
        tenant: rest.tenant,
        sub: rest.createdBy,
        ...agentData,
      };

      this.saleEdit.agentData = enhancedAgentData;
      this.saleAgentData = enhancedAgentData;
    }
    if (notifications) {
      // prepare notifications
      this.saleEdit.notifications = notifications.some(
        (n) =>
          // n.name === this.user.claims.name &&
          n.email === this.user.claims.email &&
          (n.phoneNumber === this.user.claims.phone ||
            n.phoneNumber === this.user.claims.phone_number)
      )
        ? notifications
        : [
            ...notifications,
            {
              name: this.user.claims.name,
              role: 'agent',
              enabled: false,
              locale: this.user.claims.locale,
              email: this.user.claims.email,
              phoneNumber:
                this.user.claims.phone || this.user.claims.phone_number,
            },
          ];
    }
    if (docs) {
      this.saleEdit.docs = docs;
    }
    this.saleEdit.invitedSeller = {
      data: invitedSeller,
      blocked: invitedSellerBlocked,
    };

    this.mls = mls;
    this.inviteOnly = inviteOnly;
    this.features = features;
    this.integrations = integrations;
    this.saleId = saleId || this.saleId;
    // if (!isValid && isValidExplanation) console.log(isValidExplanation);

    let hasAccess = this.hasSalePermissions;
    if (hasAccess) return;

    if (this.isMls) {
      const userTenant = this.user.profile.tenant || this.app.tenant;
      hasAccess = this.mls !== 'tenant' || rest.tenant === userTenant;
      hasAccess =
        hasAccess &&
        (!inviteOnly ||
          (inviteOnly &&
            whitelistedUsers.some((u) => u === this.user.profile.sub)));
    }

    if (!hasAccess) {
      this.socket.onAccessDenied();
    }
  }

  @action connect(uri) {
    this.socket.connect(uri);
  }

  @action disconnect() {
    this.socket.disconnect();
  }

  @computed get connected() {
    return this.socket.connected;
  }

  @computed get forbidden() {
    return this.socket.forbidden;
  }

  @computed get notFound() {
    return this.socket.notFound;
  }

  @computed get unavailable() {
    return this.socket.unavailable;
  }

  @action clearError() {
    this.error = null;
    this.round.reqError = null;
    this.offers.reqError = null;
  }

  @action clearResponse() {
    this.response = {};
  }

  @action reset() {
    this.saleId = null;
    this.isValid = false;
    this.item = {};
    this.offerData = {};
    this.agentData = {};
    this.status = defaultStatus;
    this.response = {};
    this.error = null;

    this.round.reset();
    this.offers.reset();
    this.saleEdit.reset();
    this.bidding.reset();

    this.ui.prompt.close();
    this.ui.loading.clear();
  }
}

const sale = new SaleStore(user, new WebSocketStore(user));

export default sale;
export { SaleStore };



// WEBPACK FOOTER //
// ./src/app/containers/Sale/stores/SaleStore.js